import { LEGACY_MOUNT_ID, MSPL_MOUNT_ID } from './constants';

/** Exact version accepted by the SurfaceMountV1 consumer (trim, then string compare). */
export const SURFACE_MOUNT_V1_CONTRACT_VERSION = '1' as const;

/** SurfaceMountV1 DOM attribute names. */
export const SURFACE_MOUNT_V1_ATTRIBUTES = {
  surface: 'data-fmp-surface',
  contractVersion: 'data-fmp-contract-version',
  questionId: 'data-question-id',
  routingStrategy: 'data-routing-strategy',
} as const;

/** Surfaces whose v1 mount decisions are active now. */
export const SURFACE_MOUNT_V1_ACTIVE_SURFACES = ['hub', 'question-actions'] as const;

/** Reserved vocabulary. These values deliberately fail closed until separately activated. */
export const SURFACE_MOUNT_V1_RESERVED_SURFACES = [
  'product-related-questions',
  'launcher',
] as const;

export type SurfaceMountV1ActiveSurface = (typeof SURFACE_MOUNT_V1_ACTIVE_SURFACES)[number];
export type SurfaceMountV1ReservedSurface = (typeof SURFACE_MOUNT_V1_RESERVED_SURFACES)[number];
export type SurfaceMountV1Surface = SurfaceMountV1ActiveSurface | SurfaceMountV1ReservedSurface;

/**
 * Typed renderer/consumer contract for the two active v1 surfaces.
 * The canonical id and exact contract version remain part of the producer
 * shape so a renderer cannot type-check markup that the Widget would reject.
 */
export type SurfaceMountV1 =
  | {
      readonly id: typeof MSPL_MOUNT_ID;
      readonly contractVersion: typeof SURFACE_MOUNT_V1_CONTRACT_VERSION;
      readonly surface: 'hub';
      readonly questionId?: never;
      readonly routingStrategy: 'hash' | 'path';
    }
  | {
      readonly id: typeof MSPL_MOUNT_ID;
      readonly contractVersion: typeof SURFACE_MOUNT_V1_CONTRACT_VERSION;
      readonly surface: 'question-actions';
      readonly questionId: string;
      readonly routingStrategy: 'hash' | 'path';
    };

/** The exact distinct-element candidate union pinned by ADR-028 D-1a. */
export const SURFACE_MOUNT_V1_CANDIDATE_SELECTORS = [
  `#${MSPL_MOUNT_ID}`,
  `#${LEGACY_MOUNT_ID}`,
  `[${SURFACE_MOUNT_V1_ATTRIBUTES.surface}]`,
  `[${SURFACE_MOUNT_V1_ATTRIBUTES.contractVersion}]`,
] as const;

/** Renderer-owned enhancement hooks consumed by later interaction-island slices. */
export const SURFACE_MOUNT_V1_HOOKS = {
  staticQuestion: {
    attributeName: 'data-fmp-static-question',
    value: '1',
  },
  gallery: {
    attributeName: 'data-fmp-hook',
    value: 'gallery',
  },
  acceptedAnswer: {
    attributeName: 'data-fmp-hook',
    value: 'accepted-answer',
  },
  questionActionsSlot: {
    attributeName: 'data-fmp-hook',
    value: 'question-actions-slot',
  },
} as const;

/**
 * Minimum block-size reserved synchronously by the Widget's v1 contract CSS.
 * These are implementation geometry, not new ADR-level product dimensions.
 * The hub's bounded availability-loading envelope is four 44px product-standard
 * control rows plus three 8px gaps. It remains for VISIBLE content but is
 * released on every terminal non-VISIBLE outcome, so an empty hub never leaves
 * a persistent blank region. The detail value is conditional on a
 * renderer-owned slot with an element child: one 44px product-standard control
 * target plus 10px breathing room above and below.
 */
export const SURFACE_MOUNT_V1_RESERVATION_PX = {
  hub: 200,
  'question-actions': 64,
} as const satisfies Readonly<Record<SurfaceMountV1ActiveSurface, number>>;

/** Closed UXR-006A decision vocabulary; UXR-006B owns transport and persistence. */
export const MOUNT_DECISION_CODES_V1 = [
  'contract',
  'old-id',
  'fallback-created',
  'none-excluded',
  'malformed',
  'multiple',
] as const;

export type MountDecisionCodeV1 = (typeof MOUNT_DECISION_CODES_V1)[number];
export type MountPathFamilyV1 = 'detail' | 'hub' | 'index' | 'other';

export type MountDecisionTenantIdentityV1 =
  | { readonly kind: 'store-hash'; readonly storeHash: string }
  | { readonly kind: 'unavailable' };

export type MountDecisionDeploymentIdentityV1 =
  | { readonly kind: 'deployment-id'; readonly deploymentId: string }
  | { readonly kind: 'unavailable' };

export interface MountDecisionIdentityV1 {
  readonly tenant: MountDecisionTenantIdentityV1;
  readonly deployment: MountDecisionDeploymentIdentityV1;
}

/**
 * UXR-006A emits both identity branches as `unavailable`: page/script syntax
 * cannot establish trusted non-PII provenance. UXR-006B owns the trusted binding.
 */

/**
 * Privacy-minimal, transport-neutral terminal decision event. `contract` means
 * the candidate and any surface-specific pre-mutation attestation succeeded;
 * malformed topology therefore emits `malformed`, never a false mounted event.
 * `none-excluded` covers both the index policy gate and a terminal zero-candidate
 * outcome where no eligible fallback container exists; `pathFamily` separates them.
 */
export interface MountDecisionEventV1 {
  readonly eventVersion: '1';
  readonly decision: MountDecisionCodeV1;
  readonly pathFamily: MountPathFamilyV1;
  readonly tenant: MountDecisionTenantIdentityV1;
  readonly deployment: MountDecisionDeploymentIdentityV1;
}

export interface MountDecisionEmitterV1 {
  emit(event: MountDecisionEventV1): void;
}

export const NOOP_MOUNT_DECISION_EMITTER_V1: MountDecisionEmitterV1 = Object.freeze({
  emit: (_event: MountDecisionEventV1): void => undefined,
});
